import type { TokenModel } from 'store/auth/models';
import type { Nullable } from 'types';

export type PersistKey =
  | 'accessToken'
  | 'refreshToken'
  | 'auth.mfa.required'
  | 'X-Switch-User'
  | 'X-Switch-Organisation'
  | 'organization.selected.id'
  | 'retry-lazy-refreshed'
  | 'order-proxy-current-step'
  | 'order-proxy-network-type'
  | 'order-proxy-configuration'
  | 'order-proxy-discount-code'
  | 'order-proxy-fields-configurations'
  | 'marketplace-flow'
  | 'order-origin-proxy-id'
  | 'token-expiry-time'
  | 'language'
  | 'redirect.url'
  | 'campaignParams'
  | 'auth.justSignedOut'
  | 'review.dismissed'
  | 'extend.coupon.code'
  | 'notifications.banner.dismissed'
  | 'auto-top-up.banner.dismissed'
  | 'auth.zendesk.jwt';

export type PersistStorage = 'local' | 'session';

export type PersistOptions = {
  storage?: PersistStorage;
};

export class PersistService {
  private static resolveStorage(storage: PersistStorage): Storage {
    return storage === 'session' ? sessionStorage : localStorage;
  }

  private static defaultStorage(key: PersistKey): PersistStorage {
    return key === 'campaignParams' || key === 'auth.justSignedOut' ? 'session' : 'local';
  }

  // localStorage/sessionStorage can be inaccessible (returns `null` instead of a Storage object)
  // in some Safari/WebKit contexts - restricted cross-site tracking prevention, in-app browser
  // webviews, sandboxed iframes, Lockdown Mode. Guard every access so it degrades to a
  // no-op/`null` there instead of throwing and taking down auth/boot for that user.
  private static getSafe(key: string, storage: PersistStorage): Nullable<string> {
    try {
      return this.resolveStorage(storage).getItem(key);
    } catch {
      return null;
    }
  }

  private static setSafe(key: string, value: string, storage: PersistStorage) {
    try {
      this.resolveStorage(storage).setItem(key, value);
    } catch {
      // storage unavailable - ignore
    }
  }

  private static removeSafe(key: string, storage: PersistStorage) {
    try {
      this.resolveStorage(storage).removeItem(key);
    } catch {
      // storage unavailable - ignore
    }
  }

  static get<T>(key: PersistKey, options: PersistOptions = {}): Nullable<T> {
    const storage = options.storage ?? this.defaultStorage(key);
    const value = this.getSafe(key, storage);

    if (!value) return null;

    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  static set<T>(key: PersistKey, data: Nullable<T>, options: PersistOptions = {}) {
    const storage = options.storage ?? this.defaultStorage(key);

    // Remove only when data equal to null
    if (data === null) return this.remove(key, { storage });

    this.setSafe(key, JSON.stringify(data), storage);
  }

  static remove(key: PersistKey, options: PersistOptions = {}) {
    const storage = options.storage ?? this.defaultStorage(key);
    this.removeSafe(key, storage);
  }

  static multipleRemove(keys: PersistKey[], options?: PersistOptions) {
    keys.forEach((key) => this.remove(key, options));
  }

  static getCredentials() {
    return {
      accessToken: this.getSafe('accessToken', 'local'),
      refreshToken: this.getSafe('refreshToken', 'local'),
      impersonatedEmail: this.getSafe('X-Switch-User', 'local'),
      impersonatedOrganizationId: this.getSafe('X-Switch-Organisation', 'local'),
    };
  }

  static setCredentials({ accessToken, refreshToken }: TokenModel) {
    this.setSafe('accessToken', accessToken, 'local');
    this.setSafe('refreshToken', refreshToken, 'local');
  }

  static setImpersonatedUser(email: string) {
    this.setSafe('X-Switch-User', email, 'local');
  }

  static getImpersonatedUser() {
    return this.getSafe('X-Switch-User', 'local');
  }

  static setImpersonatedOrganization(organisationId: Nullable<string>) {
    if (organisationId === null) return this.remove('X-Switch-Organisation');

    this.setSafe('X-Switch-Organisation', organisationId, 'local');
  }

  static getImpersonatedOrganization() {
    return this.getSafe('X-Switch-Organisation', 'local');
  }

  // Ends the impersonation session and clears transient state that belongs to the impersonated user.
  static clearImpersonation() {
    this.multipleRemove(['X-Switch-User', 'X-Switch-Organisation', 'campaignParams', 'redirect.url']);
  }

  static removeCredentials() {
    this.multipleRemove(['accessToken', 'refreshToken', 'X-Switch-User', 'X-Switch-Organisation', 'token-expiry-time']);
  }

  static onSignOut() {
    this.set('auth.justSignedOut', true);
    this.multipleRemove([
      'auth.mfa.required',
      'accessToken',
      'refreshToken',
      'X-Switch-User',
      'X-Switch-Organisation',
      'token-expiry-time',
      'marketplace-flow',
      'order-origin-proxy-id',
      'order-proxy-configuration',
      'order-proxy-current-step',
      'order-proxy-discount-code',
      'order-proxy-fields-configurations',
      'order-proxy-network-type',
      'review.dismissed',
      'auth.zendesk.jwt',
      'organization.selected.id',
      'campaignParams',
      'redirect.url',
    ]);
  }
}
